/**
 * Geração da placa de identificação da máquina (canvas).
 *
 * Extraído de `components/apreciacao/etapa1-dados-maquina.tsx`, onde estava
 * misturado com o upload e dependia de `apreciacaoId` já existir.
 *
 * O backend tem uma implementação equivalente em
 * `services/placa-identificacao.service.ts` (sharp + SVG), usada pelo job de
 * criação assistida — onde não há canvas nem usuário na tela. Alterações
 * visuais precisam ser feitas nos dois lados.
 */

export interface OpcoesPlacaIdentificacao {
  /** Nome/descrição da máquina. */
  descricao: string;
  /** SISMANA ou TAG. */
  sismana: string;
  logoUrl?: string;
  largura?: number;
  altura?: number;
  corFundo?: string;
  scale?: number;
}

const PADROES = {
  logoUrl: '/assets/arcelor-logo.jpg',
  largura: 620,
  altura: 320,
  corFundo: '#004191',
  scale: 2,
  bordaEspessura: 8,
  margem: 16,
  raioExterno: 22,
  raioInterno: 14,
  limiarLuminancia: 180,
};

/**
 * Carrega o logo e o converte para branco sobre fundo transparente: pixels
 * claros viram transparentes, escuros viram brancos com alfa proporcional.
 * Devolve null se o logo não estiver disponível — a placa é gerada sem ele.
 */
async function carregarLogoBranco(logoUrl: string): Promise<HTMLCanvasElement | null> {
  try {
    const logoImg = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Logo não encontrado'));
      img.src = logoUrl;
    });

    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = logoImg.naturalWidth;
    logoCanvas.height = logoImg.naturalHeight;

    const logoCtx = logoCanvas.getContext('2d');
    if (!logoCtx) return null;

    logoCtx.drawImage(logoImg, 0, 0);
    const imageData = logoCtx.getImageData(0, 0, logoCanvas.width, logoCanvas.height);
    const px = imageData.data;

    for (let i = 0; i < px.length; i += 4) {
      const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = 255;
      px[i + 1] = 255;
      px[i + 2] = 255;
      px[i + 3] = lum > PADROES.limiarLuminancia ? 0 : Math.round(255 * (1 - lum / 255));
    }

    logoCtx.putImageData(imageData, 0, 0);
    return logoCanvas;
  } catch {
    console.warn('Logo ArcelorMittal não encontrado, gerando placa sem logo');
    return null;
  }
}

/** Desenha a placa e devolve o canvas. */
export async function gerarPlacaIdentificacaoCanvas(
  opcoes: OpcoesPlacaIdentificacao
): Promise<HTMLCanvasElement> {
  const {
    descricao,
    sismana,
    logoUrl = PADROES.logoUrl,
    largura = PADROES.largura,
    altura = PADROES.altura,
    corFundo = PADROES.corFundo,
    scale = PADROES.scale,
  } = opcoes;

  const { bordaEspessura, margem, raioExterno, raioInterno } = PADROES;

  const logoCanvas = await carregarLogoBranco(logoUrl);

  const textoLinhas: string[] = [];
  if (descricao) textoLinhas.push(descricao + (sismana ? ':' : ''));
  if (sismana) textoLinhas.push(sismana);

  const canvas = document.createElement('canvas');
  canvas.width = largura * scale;
  canvas.height = altura * scale;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível obter o contexto 2D do canvas');
  ctx.scale(scale, scale);

  const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // 1. Fundo azul com cantos arredondados
  roundRect(0, 0, largura, altura, raioExterno);
  ctx.fillStyle = corFundo;
  ctx.fill();

  // 2. Borda branca interna
  roundRect(margem, margem, largura - margem * 2, altura - margem * 2, raioInterno);
  ctx.strokeStyle = 'white';
  ctx.lineWidth = bordaEspessura;
  ctx.stroke();

  // 3. Logo no canto superior direito
  if (logoCanvas) {
    const logoAltura = 40;
    const logoLargura = (logoCanvas.width / logoCanvas.height) * logoAltura;
    const logoX = largura - margem - bordaEspessura / 2 - 14 - logoLargura;
    const logoY = margem + bordaEspessura / 2 + 8;
    ctx.drawImage(logoCanvas, logoX, logoY, logoLargura, logoAltura);
  }

  // 4. Texto centralizado
  ctx.fillStyle = 'white';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const fontSize = textoLinhas.some((l) => l.length > 30) ? 28 : 34;
  ctx.font = `bold ${fontSize}px Arial, Helvetica, sans-serif`;

  const lineHeight = fontSize * 1.5;
  const totalHeight = textoLinhas.length * lineHeight;
  const startY = (altura - totalHeight) / 2 + lineHeight / 2;

  for (let i = 0; i < textoLinhas.length; i++) {
    ctx.fillText(
      textoLinhas[i],
      largura / 2,
      startY + i * lineHeight,
      largura - (margem + bordaEspessura) * 2 - 20
    );
  }

  return canvas;
}

export async function gerarPlacaIdentificacaoBlob(opcoes: OpcoesPlacaIdentificacao): Promise<Blob> {
  const canvas = await gerarPlacaIdentificacaoCanvas(opcoes);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Erro ao gerar imagem da placa'))),
      'image/png',
      1.0
    );
  });
}

export async function gerarPlacaIdentificacaoFile(opcoes: OpcoesPlacaIdentificacao): Promise<File> {
  const blob = await gerarPlacaIdentificacaoBlob(opcoes);
  return new File([blob], `identificacao_gerada_${Date.now()}.png`, { type: 'image/png' });
}

/** Data URL para pré-visualizar a placa sem fazer upload. */
export async function gerarPlacaIdentificacaoDataUrl(
  opcoes: OpcoesPlacaIdentificacao
): Promise<string> {
  const canvas = await gerarPlacaIdentificacaoCanvas(opcoes);
  return canvas.toDataURL('image/png', 1.0);
}
